import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTodayDashboardApi } from '../lib/api';
import { formatHour, formatPrice } from '../lib/format';
import { ArrowLeft, Activity, Clock, Calendar, DollarSign, Users, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function OperatorDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    loadDashboard();
  }, [user, authLoading, navigate]);

  const loadDashboard = async () => {
    try {
      const res = await getTodayDashboardApi();
      setDashboard(res.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    }
    setLoading(false);
  };

  if (authLoading || !user || loading) return null;

  const cards = [
    {
      title: 'Courts in Use',
      value: dashboard ? `${dashboard.courts_in_use} / ${dashboard.total_courts}` : '—',
      subtitle: 'Right now',
      icon: Activity,
      color: 'bg-brand-teal text-white',
      iconBg: 'bg-white/20',
    },
    {
      title: 'Next Transition',
      value: dashboard?.next_transition_hour ? formatHour(dashboard.next_transition_hour) : 'No transitions',
      subtitle: 'Upcoming change',
      icon: Clock,
      color: 'bg-white text-gray-900',
      iconBg: 'bg-brand-teal-tint',
    },
    {
      title: "Today's Booked Hours",
      value: dashboard ? `${dashboard.total_booked_hours}h` : '—',
      subtitle: `${dashboard?.total_bookings || 0} bookings`,
      icon: Calendar,
      color: 'bg-white text-gray-900',
      iconBg: 'bg-pickle-lime/30',
    },
    {
      title: "Today's Revenue",
      value: dashboard ? formatPrice(dashboard.total_revenue) : '—',
      subtitle: dashboard?.pending_revenue > 0 ? `${formatPrice(dashboard.pending_revenue)} pending` : 'All collected',
      icon: DollarSign,
      color: 'bg-white text-gray-900',
      iconBg: 'bg-brand-teal-tint',
    },
    {
      title: 'Open Play Sessions',
      value: dashboard?.open_play_sessions || 0,
      subtitle: 'Active today',
      icon: Users,
      color: 'bg-white text-gray-900',
      iconBg: 'bg-open-play-tint',
    },
  ];

  return (
    <div className="min-h-screen bg-app-bg" data-testid="operator-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-black/5 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <button
            data-testid="dashboard-back-btn"
            onClick={() => navigate('/operator')}
            className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="font-heading text-xl font-bold text-gray-900">Facility Dashboard</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="dashboard-cards">
          {cards.map((card, i) => {
            const Icon = card.icon;
            const isHero = i === 0;
            return (
              <div
                key={card.title}
                data-testid={`dashboard-card-${i}`}
                className={`rounded-3xl p-6 border border-black/5 transition-all hover:shadow-md ${
                  isHero ? 'bg-brand-teal text-white md:col-span-2 lg:col-span-1' : 'bg-white'
                }`}
                style={isHero ? {} : {}}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${card.iconBg}`}>
                    <Icon className={`w-5 h-5 ${isHero ? 'text-white' : 'text-brand-teal'}`} />
                  </div>
                </div>
                <p className={`font-heading text-3xl font-bold mb-1 ${isHero ? 'text-white' : 'text-gray-900'}`}>
                  {card.value}
                </p>
                <p className={`text-sm font-medium ${isHero ? 'text-white/80' : 'text-gray-600'}`}>{card.title}</p>
                <p className={`text-xs mt-1 ${isHero ? 'text-white/60' : 'text-gray-400'}`}>{card.subtitle}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
